<template>
    <v-container fluid class="pa-0">
        <model-list-container modelName="Role" @create="createRole">
            <template v-slot:description>
                Roles are used to group individual permissions together. Groups can then be assigned
                to a Role.
            </template>
            <template v-slot:table>
                <v-data-table
                    :headers="roleHeaders"
                    :items="roles"
                    class="elevation-1"
                    clearable
                    :disabled="isLoading"
                    :loading="isLoading"
                    :items-per-page="25"
                >
                    <template v-slot:item.permissions="{ item }">
                        <v-row no-gutters>
                            <v-col
                                v-for="(permission, index) in item.permissions"
                                :key="index"
                                sm="6"
                            >
                                <div class="permission">
                                    {{ permission.resource }}:{{ permission.action }}
                                </div>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- 'Actions' column -->
                    <template v-slot:item.actions="{ item }">
                        <model-actions
                            :item="item"
                            @edit="editRole"
                            @delete="deleteRole"
                        ></model-actions>
                    </template>
                </v-data-table>
            </template>
        </model-list-container>
        <model-list-container modelName="Group" @create="createGroup">
            <template v-slot:description>
                Groups are used to assign a Role to a set of users to grant them permissions. To
                assign a group to a user, use
                <a :href="awsCognitoUserPoolPage" target="_blank">AWS Cognito.</a>
            </template>
            <template v-slot:table>
                <v-data-table
                    :headers="groupHeaders"
                    :items="groups"
                    class="elevation-1"
                    clearable
                    :disabled="isLoading"
                    :loading="isLoading"
                    :items-per-page="25"
                >
                    <template v-slot:item.role="{ item }">
                        <v-container>
                            {{ item.role.name }}
                        </v-container>
                    </template>

                    <template v-slot:item.benchlingProjects="{ item }">
                        <v-container v-if="item.benchlingProjects === null"> None </v-container>
                        <v-row v-else no-gutters>
                            <v-col
                                v-for="(benchlingProject, index) in item.benchlingProjects"
                                :key="index"
                                align-items="center"
                            >
                                <a :href="benchlingProject.url" target="_blank">{{
                                    benchlingProject.name
                                }}</a>
                            </v-col>
                        </v-row>
                    </template>

                    <template v-slot:item.platforms="{ item }">
                        <v-container v-if="item.platforms === null"> None </v-container>
                        <v-col
                            v-else
                            v-for="(platform, index) in item.platforms"
                            :key="index"
                            align-items="center"
                        >
                            {{ platform.name }}
                        </v-col>
                    </template>

                    <!-- 'Actions' column -->
                    <template v-slot:item.actions="{ item }">
                        <model-actions
                            :item="item"
                            @edit="editGroup"
                            @delete="deleteGroup"
                        ></model-actions>
                    </template>
                </v-data-table>
            </template>
        </model-list-container>
    </v-container>
</template>

<script>
import { defineComponent } from "vue";

import ModelListContainer from "../common/models/ModelListContainer.vue";
import ModelActions from "../common/models/ModelActions.vue";
import { roleService } from "../../lib/admin/roleAdmin";
import { groupService } from "../../lib/admin/groupAdmin";
import config from "../../config";

export default defineComponent({
    name: "AuthAdmin",
    components: { ModelActions, ModelListContainer },
    props: {},

    async mounted() {
        this.roles = await roleService.fetchAll();
        this.groups = await groupService.fetchAll();
        this.isLoading = false;
    },

    computed: {},

    methods: {
        editGroup(group) {
            this.$router.push(`/auth/groups/edit/${group.id}`);
        },
        createGroup() {
            this.$router.push(`/auth/groups/edit`);
        },
        async deleteGroup(group) {
            if (confirm(`Are you sure you want to delete group ${group.name}?`)) {
                await groupService.delete(group);
            }
            this.groups = await groupService.fetchAll();
        },
        editRole(role) {
            this.$router.push(`/auth/roles/edit/${role.id}`);
        },
        createRole() {
            this.$router.push(`/auth/roles/edit`);
        },
        async deleteRole(role) {
            if (confirm(`Are you sure you want to delete role ${role.name}?`)) {
                await roleService.delete(role);
            }
            this.roles = await roleService.fetchAll();
        },
    },

    data() {
        return {
            awsCognitoUserPoolPage: `"https://us-east-1.console.aws.amazon.com/cognito/v2/idp/user-pools/${config.cognitoUserPoolId}/groups?region=us-east-1"`,
            isLoading: true,
            roles: [],
            groups: [],
            roleHeaders: [
                {
                    title: "Name",
                    value: "name",
                    align: "center",
                    sortable: true,
                    width: "20",
                },
                {
                    title: "Permissions",
                    value: "permissions",
                    align: "center",
                    sortable: true,
                    width: "100",
                },
                {
                    title: "Actions",
                    value: "actions",
                    width: "50",
                },
            ],
            groupHeaders: [
                {
                    title: "Name",
                    value: "name",
                    align: "center",
                    sortable: true,
                    width: "15",
                },
                {
                    title: "Description",
                    value: "description",
                    align: "center",
                    sortable: false,
                    width: "25",
                },
                {
                    title: "Role",
                    value: "role",
                    align: "center",
                    sortable: true,
                    width: "100",
                },
                {
                    title: "Benchling Projects",
                    value: "benchlingProjects",
                    align: "center",
                    sortable: true,
                    width: "75",
                },
                {
                    title: "Platforms",
                    value: "platforms",
                    align: "center",
                    sortable: true,
                    width: "100",
                },
                {
                    title: "Actions",
                    value: "actions",
                    width: "25",
                },
            ],
        };
    },
});
</script>

<style>
.permission {
    padding: 5px;
}
</style>
